import React, { useEffect, useCallback, useRef } from 'react';
import styles from './VideoModal.module.css';

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoUrl: string;
  title?: string;
}

const isLocalVideo = (url: string) => /\.(mp4|webm|ogv|mov)(\?|$)/i.test(url);

export const VideoModal: React.FC<VideoModalProps> = ({
  isOpen,
  onClose,
  videoUrl,
  title = 'Video',
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleClose = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    onClose();
  }, [onClose]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    },
    [handleClose]
  );

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const isVideo = isLocalVideo(videoUrl);

  // Add autoplay parameter to external embed URL
  const videoUrlWithAutoplay = videoUrl.includes('?')
    ? `${videoUrl}&autoplay=1`
    : `${videoUrl}?autoplay=1`;

  return (
    <div
      className={styles.overlay}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className={styles.container}>
        <button
          className={styles.closeButton}
          onClick={handleClose}
          aria-label="Close video"
          type="button"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <div className={styles.videoWrapper}>
          {isVideo ? (
            <video
              ref={videoRef}
              className={styles.video}
              controls
              autoPlay
              playsInline
            >
              <source
                src={videoUrl}
                type={`video/${videoUrl.split('.').pop()}`}
              />
            </video>
          ) : (
            <iframe
              src={videoUrlWithAutoplay}
              style={{ backgroundColor: '#fff' }}
              title={title}
              className={styles.iframe}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          )}
        </div>
      </div>
    </div>
  );
};
